import {
  ConfirmPurchaseModalFormValues,
  PurchaseItems,
} from '@/core/domain/purchase/purchaseImport/PurchaseImport.types';
import { Form, Modal, ModalProps, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import ConfirmImportPurchaseSteps from './ConfirmImportPurchaseSteps';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { useCompanySettings } from '@/core/domain/enterprise/Companies/Companies.hooks';

type Props = Omit<ModalProps, 'onCancel' | 'onOk'> & {
  companyId?: number;
  onCancel: VoidFunction;
  items?: PurchaseItems[];
  onOk: (items: PurchaseItems[]) => void;
};

const PurchaseImportConfirmModal: React.FC<Props> = ({
  open,
  onCancel,
  items,
  onOk,
  companyId,
  ...props
}) => {
  const [form] = Form.useForm<ConfirmPurchaseModalFormValues>();
  const matrix = useMatrix();
  const hasUpdateNcm =
    matrix.data?.matrixSettings.updateNcmAttributesOnExternalImports;

  const companySettings = useCompanySettings(companyId);
  const isOutputCfopBlockedInPurchaseImport =
    companySettings.data?.isOutputCfopBlockedInPurchaseImport;

  const [unlinkItems, setUnlinkItems] = useState<PurchaseItems[]>([]);
  const [asUnlinkItems, setAsUnlinkItems] = useState<boolean>(false);

  useEffect(() => {
    if (items) {
      setAsUnlinkItems(items?.every((item) => item.isAlter === false));
    }
  }, [items]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  useEffect(() => {
    if (items) {
      const formItems = items.map((item) => ({
        ...item,
        entryCode: item.cfop.code,
        purchaseItemTaxes: {
          ...item.purchaseItemTaxes,
          cstPis: hasUpdateNcm ? item.purchaseItemTaxes?.cstPis : '',
          cstCofins: hasUpdateNcm ? item.purchaseItemTaxes?.cstCofins : '',
          cstIpi: hasUpdateNcm ? item.purchaseItemTaxes?.cstIpi : '',
        },
      }));

      form.setFieldsValue({
        itemsCfop:
          formItems.reduce(
            (acc, curr) =>
              acc.some(
                (item: PurchaseItems) => item.cfop?.code === curr.cfop?.code,
              )
                ? acc
                : [...acc, curr],
            [] as PurchaseItems[],
          ) ?? [],

        itemsNcm:
          formItems.reduce(
            (acc, curr) =>
              acc.some(
                (item: PurchaseItems) => item.ncm?.code === curr.ncm?.code,
              )
                ? acc
                : [...acc, curr],
            [] as PurchaseItems[],
          ) ?? [],
      });

      setUnlinkItems(items.filter((item) => !item.linked));
    }
  }, [items, matrix.dataUpdatedAt]);

  const handleOk = async () => {
    try {
      const formValues = await form.validateFields();

      const itemsCfop = formValues?.itemsCfop;
      const itemsNcm = formValues?.itemsNcm;

      const unlinkItemsMapped = unlinkItems?.map((item) => {
        const itemCfop = itemsCfop?.find(
          (fieldItem) => fieldItem.cfop.code === item.cfop.code,
        );

        const itemNcm = itemsNcm?.find(
          (fieldItem) => fieldItem.ncm.code === item.ncm.code,
        );

        const unlinkItem = unlinkItems?.find(
          (fieldItem) => fieldItem.index === item.index,
        );

        let newItem = item;

        if (itemCfop) {
          newItem = { cfopId: itemCfop.cfopId, ...newItem };
        }

        if (itemNcm) {
          newItem = {
            ...newItem,
            ncmId: itemNcm.ncmId,
            purchaseItemTaxes: {
              ...newItem.purchaseItemTaxes,
              ...itemNcm.purchaseItemTaxes,
            },
          };
        }

        if (unlinkItem) {
          newItem = {
            ...newItem,
            cfop: { ...newItem.cfop },
            manufacturerId: unlinkItem.manufacturerId,
            subGroupId: unlinkItem.subGroupId,
          };
        }

        return newItem;
      });

      onOk(
        items?.map((item) => {
          const itemFinded = unlinkItemsMapped.find(
            (unlinkItem) => unlinkItem.key === item.key,
          );

          const cfopSelected = itemsCfop.find(
            (fieldItem) => item.cfop.code === fieldItem.entryCode,
          );

          const purchaseItemTaxes = itemsNcm?.find(
            (fieldItem) => fieldItem.ncm.code === item.ncm.code,
          )?.purchaseItemTaxes;

          const response = {
            ...item,
            ...itemFinded,
            cfopId: cfopSelected?.id,
            cfop: { ...cfopSelected?.cfop },
            purchaseItemTaxes: { ...purchaseItemTaxes },
          } as PurchaseItems;

          return response;
        }) || [],
      );
    } catch (error) {
      notification.error({
        message: 'Campos obrigatórios',
        description:
          "Por favor, preencha todos os campos dos CFOP's referente a entrada",
      });
    }
  };

  return (
    <Modal
      width={1400}
      open={open}
      onCancel={handleCancel}
      title={'Confirmação da importação'}
      footer={null}
    >
      <Form form={form}>
        <ConfirmImportPurchaseSteps
          onCancel={handleCancel}
          onCallback={handleOk}
          asUnlinkItems={asUnlinkItems}
          unlinkItems={unlinkItems}
          setUnlinkItems={setUnlinkItems}
          confirmLoading={props.confirmLoading}
          hasOutputCfopOnImportPurchase={isOutputCfopBlockedInPurchaseImport}
        />
      </Form>
    </Modal>
  );
};

export default PurchaseImportConfirmModal;
